import { apiFetch } from "./api";
import type { Paginated } from "./platform-api";

export type CorrectionRequestStatus =
  | "PENDING"
  | "APPROVED"
  | "REJECTED"
  | "NEEDS_CLARIFICATION"
  | "CANCELLED";
export type CorrectionRequestType =
  | "MISSED_CHECK_IN"
  | "MISSED_CHECK_OUT"
  | "INCORRECT_LOCATION"
  | "ATTENDANCE_CORRECTION"
  | "LEAVE"
  | "OVERTIME"
  | "SHIFT_SWAP"
  | "WORK_FROM_ANYWHERE"
  | "OTHER";

/**
 * One request attachment (FEAT-2026-008). `url` is a **stable** API URL that
 * 302-redirects to a fresh short-lived signed GET — safe to store/open; every
 * open is access-checked server-side.
 */
export interface RequestAttachment {
  url: string;
  filename: string;
  mimeType: string;
  bytes: number;
}

/**
 * Structured detail the mobile app attaches to a request, versioned by `v`.
 * Every sub-object is optional — only the keys relevant to the request's type
 * are present, and older rows may carry none of them. Treat every field as
 * possibly-absent: this is client-supplied and stored verbatim, so the admin
 * console must render defensively and never assume a shape.
 *
 * Contract: `docs/mobile/requests.md` → "Request metadata".
 */
export interface RequestMetadata {
  v?: number;
  /** OVERTIME. 24-hour clock times on the request's `date`. */
  overtime?: {
    startTime?: string;
    endTime?: string;
    durationMinutes?: number;
    /** True for a shift crossing midnight, e.g. 22:00 → 02:00. */
    endsNextDay?: boolean;
  };
  /** INCORRECT_LOCATION. The site the employee says should have been recorded. */
  correctedSite?: {
    siteId?: string;
    siteName?: string;
  };
  /** Correction types. Snapshot of the record at submit time. */
  recordedState?: {
    date?: string;
    firstCheckInAt?: string;
    lastCheckOutAt?: string;
    siteName?: string;
    totalWorkedMinutes?: number;
  };
  [key: string]: unknown;
}

export interface CorrectionRequest {
  id: string;
  tenantId: string;
  employeeId: string;
  employeeName: string;
  employeeEmpCode: string;
  managerId: string | null;
  managerName: string | null;
  managerEmail: string | null;
  /** Primary date (first day for multi-day leave/shift-swap). */
  date: string;
  /** Last day of a range: Leave, Work from anywhere, Other in range mode, and past Shift swaps. Null for single-day. */
  dateTo: string | null;
  type: CorrectionRequestType;
  /** The tenant's name for the type, or null (FEAT-2026-031). */
  typeLabel?: string | null;
  submittedById: string;
  currentState: string | null;
  requestedChange: string;
  reason: string;
  /** Machine-readable detail; null on older rows and on types that carry none. */
  metadata: RequestMetadata | null;
  attachments: RequestAttachment[];
  status: CorrectionRequestStatus;
  approverId: string | null;
  approvedAt: string | null;
  approvalNotes: string | null;
  /** Who made the current decision (FEAT-2026-027). Null unless decided. */
  decidedByName: string | null;
  decidedByRole: "LINE_MANAGER" | "HR" | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListRequestsQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: CorrectionRequestStatus;
  type?: CorrectionRequestType;
  employeeId?: string;
  assignedToMe?: boolean;
}

/** The list's filters, without paging. */
export type RequestSummaryQuery = Omit<ListRequestsQuery, "page" | "pageSize">;

/** Status counts over every request the filters match, not one page. */
export interface RequestSummary {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  needsClarification: number;
}

export interface DecisionBody {
  notes?: string;
}

export interface BulkApproveBody {
  ids: string[];
  notes?: string;
}

export interface BulkApproveResult {
  approved: string[];
  skipped: Array<{ id: string; reason: string }>;
  totalProcessed: number;
}

function toQuery(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "" || v === false) continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

export const requestsApi = {
  list: (q: ListRequestsQuery = {}) =>
    apiFetch<Paginated<CorrectionRequest>>(`/requests${toQuery(q as Record<string, unknown>)}`),

  summary: (q: RequestSummaryQuery = {}) =>
    apiFetch<RequestSummary>(`/requests/summary${toQuery(q as Record<string, unknown>)}`),

  get: (id: string) => apiFetch<CorrectionRequest>(`/requests/${id}`),

  approve: (id: string, body: DecisionBody = {}) =>
    apiFetch<CorrectionRequest>(`/requests/${id}/approve`, { method: "PATCH", body }),

  reject: (id: string, body: DecisionBody = {}) =>
    apiFetch<CorrectionRequest>(`/requests/${id}/reject`, { method: "PATCH", body }),

  needsClarification: (id: string, body: DecisionBody = {}) =>
    apiFetch<CorrectionRequest>(`/requests/${id}/needs-clarification`, { method: "PATCH", body }),

  bulkApprove: (body: BulkApproveBody) =>
    apiFetch<BulkApproveResult>(`/requests/bulk-approve`, { method: "PATCH", body }),
};
